import { createAction } from '@reduxjs/toolkit'
import {
  BuildingViewMode,
  Storey,
  Roof,
  BuildingElementType,
  BuildingElementSubType,
  BuildingElementStatus,
  ConvertData,
  Wall,
  Window,
  BuildingOpeningSize,
  Door,
  BuildingDocument,
  BuildingBeamSize,
  Vertex3D,
  GridSettings,
  BuildingTool,
  HideShowOption,
} from '../models/Construction'
import {
  ConstructionPage,
  ConstructionTab,
  EditorTab,
} from '../reducers/construction'
import { StoreyElementsVisibility } from '../utils/EventEmitter'
import {
  BuildingStructuralTypes,
  RectangleFramingElement,
  RectangleFramingElementType,
} from '../models/Structural'
import { LoadingPage } from '../models/File'
import { WallAnchor } from '../visualization/tools/useEditWallTool'
import { BimObject } from '../models/Bim'

export const setConvertData = createAction<ConvertData>(
  'building/setConvertData',
)
export const setFileName = createAction<string>('building/setFileName')
export const setBuildingDocument = createAction<BuildingDocument>(
  'building/setBuildingDocument',
)
export const updateBuildingDocumentPage = createAction<{
  fileId: string
  page: LoadingPage
}>('building/updateDocumentPage')
export const updateBuildingDocumentSelectedPage = createAction<{
  fileId: string | undefined
  pageId: string
}>('building/updateDocumentSelectedlPage')
export const updateConvertData = createAction<{
  convertData: ConvertData
  elementId: string | null
}>('building/updateConvertData')
export const updateStatusMessage = createAction<string>(
  'building/updateStatusMessage',
)
export const resetLastUpdatedElementId = createAction(
  'building/resetLastUpdatedElementId',
)
export const addStoreys = createAction<{ storeys: Storey[] }>(
  'building/addStoreys',
)
export const setBuildigBimObjects = createAction<BimObject[]>(
  'building/setBimObjects',
)

export type CreateWallPayload = Pick<
  Wall,
  | 'id'
  | 'type'
  | 'length'
  | 'height'
  | 'thickness'
  | 'angle'
  | 'bottom_elevation'
  | 'center_point'
> & { storeyId?: string }
export const createWall = createAction<CreateWallPayload>('building/createWall')

type EditWallLengthPayload = Pick<Wall, 'id' | 'length' | 'center_point'> & {
  anchor: WallAnchor
}
type EditWallThicknessPayload = Pick<Wall, 'id' | 'thickness' | 'center_point'>
type EditWallPositionPayload = Pick<Wall, 'id' | 'center_point'>
export type EditWallPayload =
  | EditWallLengthPayload
  | EditWallThicknessPayload
  | EditWallPositionPayload
export const editWall = createAction<EditWallPayload>('building/editWall')
export const removeWall = createAction<{ wallId: string }>(
  'building/removeWall',
)

export type CreateWindowPayload = Pick<
  Window,
  | 'id'
  | 'wall_id'
  | 'type'
  | 'width'
  | 'height'
  | 'thickness'
  | 'center_location_on_wall'
  | 'sill_height'
>
export const createWindow = createAction<CreateWindowPayload>(
  'building/createWindow',
)
export type EditWindowPayload = Pick<
  Window,
  | 'id'
  | 'wall_id'
  | 'width'
  | 'height'
  | 'sill_height'
  | 'center_location_on_wall'
>
export const editWindow = createAction<EditWindowPayload>('building/editWindow')
export const removeWindow = createAction<{ windowId: string }>(
  'building/removeWindow',
)

export type CreateDoorPayload = Pick<
  Door,
  | 'id'
  | 'wall_id'
  | 'type'
  | 'width'
  | 'height'
  | 'thickness'
  | 'center_location_on_wall'
  | 'sill_height'
>
export const createDoor = createAction<CreateDoorPayload>('building/createDoor')
export type EditDoorPayload = Pick<
  Door,
  | 'id'
  | 'wall_id'
  | 'width'
  | 'height'
  | 'sill_height'
  | 'center_location_on_wall'
>
export const editDoor = createAction<EditDoorPayload>('building/editDoor')
export const removeDoor = createAction<{ doorId: string }>(
  'building/removeDoor',
)

export const createStructuralElement = createAction<{
  element: RectangleFramingElement
  elementType: RectangleFramingElementType
  storeyId: string
}>('building/createStructuralElement')
export type EditStructuralElementPayload = Pick<
  RectangleFramingElement,
  'id' | 'length' | 'center_point'
> & { elementType: RectangleFramingElementType }
export const editStructuralElement = createAction<EditStructuralElementPayload>(
  'building/editStructuralElement',
)
export const removeStructuralElement = createAction<{
  elementId: string
  structuralType: BuildingStructuralTypes
}>('building/removeStructuralElement')

export const setShowUploadModal = createAction<boolean>(
  'building/setShowUploadModal',
)
export const setShowProgressModal = createAction<boolean>(
  'building/setShowProgressModal',
)
export const addRoof = createAction<{ roof: Roof }>('building/addRoof')
export const setSelectedStoreyId = createAction<string | null>(
  'building/setSelectedStoreyId',
)

export const setSelectedElementsIds = createAction<string[]>(
  'building/setSelectedElementsIds',
)

export const setSelectedBuildingMode = createAction<{ mode: BuildingViewMode }>(
  'building/setSelectedBuildingMode',
)

export const setSelectedConstructionTab = createAction<ConstructionTab>(
  'building/setSelectedConstructionTab',
)

export const setSelectedConstructionPage = createAction<ConstructionPage>(
  'building/setSelectedConstructionPage',
)

export const setSelectedEditorTab = createAction<EditorTab>(
  'building/setSelectedEditorTab',
)

export const setSelectedTool = createAction<BuildingTool | null>(
  'building/setSelectedTool',
)
type SelectedToolPropsPayload = Partial<{
  wallThickness: number
  wallType: BuildingElementSubType
  windowSize: BuildingOpeningSize
  doorSize: BuildingOpeningSize
  beamSize: BuildingBeamSize
}>
export const setSelectedToolProps = createAction<SelectedToolPropsPayload>(
  'building/setSelectedToolProps',
)

export const resetBuildingElementsStatus = createAction(
  'building/resetBuildingElementsStatus',
)
export const resetConstructionState = createAction(
  'building/resetConstructionState',
)

export const toggleHideShowOption = createAction<HideShowOption>(
  'building/toggleHideShowOption',
)
export const setHideShowOption = createAction<{
  option: HideShowOption
  isEnabled: boolean
}>('building/setHideShowOption')

export const updateHiddenStoreyElements =
  createAction<StoreyElementsVisibility>('building/updateHiddenStoreyElements')

export const setIs3DViewActive = createAction<boolean>(
  'organization/setIs3DViewActive',
)
export const setIs2DViewActive = createAction<boolean>(
  'organization/setIs2DViewActive',
)
export const setIs3DFetchBuildingInfo = createAction<boolean>(
  'organization/setIs3DFetchBuildingInfo',
)
export const updateBuildingElementStatus = createAction<{
  elementType: BuildingElementType
  elementSubtype?: BuildingElementSubType
  status: BuildingElementStatus
}>('building/updateBuildingElementStatus')
export const updateGridSettings = createAction<Partial<GridSettings>>(
  'building/updateGridSettings',
)

export type MoveRoofPayload = {
  id: string
  offset: Vertex3D
}
export const moveRoof = createAction<MoveRoofPayload>('building/moveRoof')
